import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Tooltip } from "react-tooltip";

import { AppWrap, MotionWrap } from "../../wrapper";
import { urlFor, client } from "../../client";
import "./Skills.scss";

const monthMap = {
  JAN: 1,
  FEB: 2,
  MAR: 3,
  APR: 4,
  MAY: 5,
  JUN: 6,
  JUL: 7,
  AUG: 8,
  SEP: 9,
  OCT: 10,
  NOV: 11,
  DEC: 12,
};

function parseLeftDateFromRange(str) {
  if (!str || typeof str !== "string") return null;

  // If the range is inside parentheses, prefer that content:
  const parenMatch = str.match(/\(([^)]+)\)/);
  const source = parenMatch ? parenMatch[1] : str;

  // Normalize whitespace
  const s = source.trim();

  // 1) Match MON YYYY (e.g. SEP 2024 - ...)
  let m = s.match(/^([A-Za-z]{3})\s+(\d{4})/);
  if (m) {
    const mon = m[1].toUpperCase();
    const yr = parseInt(m[2], 10);
    const month = monthMap[mon];
    if (month) return new Date(yr, month - 1, 1);
  }

  // 2) Match MM/YYYY (e.g. 11/2023 - ...)
  m = s.match(/^(\d{1,2})\/(\d{4})/);
  if (m) {
    const month = parseInt(m[1], 10);
    const yr = parseInt(m[2], 10);
    if (!Number.isNaN(month) && !Number.isNaN(yr))
      return new Date(yr, month - 1, 1);
  }

  // 3) Match full words like "September 2024" (fall-back)
  m = s.match(/^([A-Za-z]+)\s+(\d{4})/);
  if (m) {
    const monWord = m[1].slice(0, 3).toUpperCase();
    const yr = parseInt(m[2], 10);
    const month = monthMap[monWord];
    if (month) return new Date(yr, month - 1, 1);
  }

  return null;
}

function isCurrentRange(str) {
  if (!str || typeof str !== "string") return false;
  return (
    /-\s*(CURRENT|PRESENT)\s*$/i.test(str) ||
    /\((?:CURRENT|PRESENT)\)$/i.test(str)
  );
}

function compareByRange(aRangeStr, bRangeStr) {
  // current first
  const aCurr = isCurrentRange(aRangeStr) ? 1 : 0;
  const bCurr = isCurrentRange(bRangeStr) ? 1 : 0;
  if (aCurr !== bCurr) return bCurr - aCurr;

  // then newest start date first
  const aDate = parseLeftDateFromRange(aRangeStr);
  const bDate = parseLeftDateFromRange(bRangeStr);
  if (!aDate && !bDate) return 0;
  if (!aDate) return 1;
  if (!bDate) return -1;
  return bDate - aDate;
}

const SkillsItem = ({ skill }) => (
  <motion.div
    whileInView={{ opacity: [0, 1] }}
    transition={{ duration: 0.5 }}
    className="app__skills-item app__flex"
    key={skill.name}
  >
    <div className="app__flex" style={{ backgroundColor: skill.bgColor }}>
      <img src={urlFor(skill.icon)} alt={skill.name} />
    </div>
    <p className="p-text">{skill.name}</p>
  </motion.div>
);

const Skills = () => {
  const [experiences, setExperiences] = useState([]);
  const [skills, setSkills] = useState([]);

  useEffect(() => {
    const query = '*[_type == "experiences"]';
    const skillsQuery = '*[_type == "skills"]';

    client.fetch(skillsQuery).then((data) => {
      setSkills(data || []);
    });

    client.fetch(query).then((data) => {
      // data might be an array of documents; if your experiences are stored as a single doc with a works array,
      // you may get [ { _id, works: [...] } ] — handle both shapes.

      let items = Array.isArray(data) ? data : [];

      // If experiences are stored as a single document with `works` as array,
      // try to unwrap a common pattern: a single doc that contains 'works' array.
      // But we assume each item has a 'year' property as in your UI.
      // Filter only items that look like experiences with a year.
      items = items.filter((item) => item && item.year);

      // Sort the outer experiences by their `year` string (current first, then newest start)
      items.sort((a, b) => compareByRange(a.year, b.year));

      // Also sort `works` inside each experience if possible by parsing dates inside work.name or work.year
      const sortedItems = items.map((exp) => {
        const works = Array.isArray(exp.works) ? [...exp.works] : [];

        works.sort((w1, w2) => {
          // try to find a date on work.year or inside work.name parentheses
          const left1 = w1.year || w1.date || w1.name || "";
          const left2 = w2.year || w2.date || w2.name || "";
          return compareByRange(left1, left2);
        });

        return { ...exp, works };
      });

      setExperiences(sortedItems);
    });
  }, []);

  return (
    <>
      <h2 className="head-text">Skills & Experiences</h2>

      <div className="app__skills-container">
        <motion.div className="app__skills-list">
          {skills.map((skill) => (
            <SkillsItem skill={skill} key={skill.name} />
          ))}
        </motion.div>
        <div className="app__skills-exp">
          {experiences.map((experience) => (
            <motion.div
              className="app__skills-exp-item"
              key={experience._id || experience.year}
            >
              <div className="app__skills-exp-year">
                <p className="bold-text">{experience.year}</p>
              </div>
              <motion.div className="app__skills-exp-works">
                {experience.works &&
                  experience.works.map((work) => (
                    <React.Fragment key={work._key || work.name}>
                      <motion.div
                        whileInView={{ opacity: [0, 1] }}
                        transition={{ duration: 0.5 }}
                        className="app__skills-exp-work"
                        data-tip
                        data-for={work.name}
                      >
                        <h4 className="bold-text">{work.name}</h4>
                        <p className="p-text">{work.company}</p>
                      </motion.div>
                      <Tooltip
                        id={work.name}
                        effect="solid"
                        arrowColor="#fff"
                        className="skills-tooltip"
                      >
                        {work.desc}
                      </Tooltip>
                    </React.Fragment>
                  ))}
              </motion.div>
            </motion.div>
          ))}
        </div>
      </div>
    </>
  );
};

export default AppWrap(
  MotionWrap(Skills, "app__skills"),
  "skills",
  "app__whitebg"
);
